import { Button } from '@/components/ui/button';
import { PageLink } from '@/components/PageLink';
import { ActionTags } from '@/shared/components/action-tags';
import { SelectReferralSource } from '@/components/SelectReferralSource';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { SelectClientRelationshipStage } from '@/components/SelectClientRelationshipStage';
import { OnboardingOverview } from '@/pages/OnboardingOverview';

import { Mail, MoreVertical, ChevronDown, Users, FileTextIcon, FlaskConical } from 'lucide-react';
import { Badge } from '@design-system';

import { useDispatch } from 'react-redux';
import { useNavigateToPage } from '@client';
import { useCallback, useMemo, useState } from 'react';

import {
  getClientAdvisor,
  clientCanViewResults,
  clientPartnerFullName,
  getAdvisorOrganization,
} from '@/components/ClientsTable';
import { fullName as sharedFullName } from '@shared';
import { putClientReferralSourceByClientIdRequest } from '@client/model';
import { cn, ModalState, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';
import { IncompleteIntakeDialog } from '@/shared/components/incomplete-intake-dialog';
import { clientNeedsFullIntakeForm } from '@/shared/lib/utils';

interface ClientCardProps {
  client: Client;
  clients: Client[];
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
  onMenuClick: () => void;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}

function ClientNameWrapper({
  client,
  sessionAdvisor,
  children,
}: {
  client: Client;
  sessionAdvisor: Advisor;
  children: React.ReactNode;
}) {
  const navigateToPage = useNavigateToPage();
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);

  const clientTags = client.clientTags;
  const canViewResults = clientCanViewResults(clientTags);
  const isDifferentAdvisor = sessionAdvisor.advisorId !== client.advisorId;
  const needsFullIntake = clientNeedsFullIntakeForm(client);

  const handleNavigate = useCallback(() => {
    if (needsFullIntake) {
      setIncompleteDialogOpen(true);
    } else {
      navigateToPage(OnboardingOverview, { clientId: client.clientId });
    }
  }, [navigateToPage, client.clientId, needsFullIntake]);

  const wrapper = (node: React.ReactNode) => (
    <div className="w-full">
      {node}
      <IncompleteIntakeDialog
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
        client={client}
      />
    </div>
  );

  if (!canViewResults) {
    return wrapper(children);
  }

  if (isDifferentAdvisor) {
    return wrapper(
      <AdvisorWarningDialog
        title="Viewing another advisor's client"
        message="Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
        onContinue={handleNavigate}
      >
        {children}
      </AdvisorWarningDialog>,
    );
  }

  if (needsFullIntake) {
    return wrapper(
      <span
        onClick={() => setIncompleteDialogOpen(true)}
        className="cursor-pointer"
      >
        {children}
      </span>,
    );
  }

  return wrapper(
    <PageLink
      to={OnboardingOverview}
      targetProps={{ clientId: client.clientId }}
    >
      {children}
    </PageLink>,
  );
}

export function ClientCard({
  client,
  clients,
  onMenuClick,
  sessionAdvisor,
  subordinateAdvisors,
  setModalState,
}: ClientCardProps) {
  const dispatch = useDispatch();
  const navigateToPage = useNavigateToPage();

  const [isExpanded, setIsExpanded] = useState(false);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const needsFullIntake = clientNeedsFullIntakeForm(client);

  const clientName = sharedFullName(client) || '-';
  const partner = clientPartnerFullName(client, clients);
  const org = getAdvisorOrganization(client.advisorId, subordinateAdvisors, sessionAdvisor);

  const hasErrorTag = client.clientTags.some(t => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(t));

  const canShowPrimaryAction = useMemo(
    () => clientCanViewResults(client.clientTags) && !hasErrorTag,
    [client.clientTags, hasErrorTag],
  );

  const handleViewResults = useCallback(() => {
    if (needsFullIntake) {
      setIncompleteDialogOpen(true);
    } else {
      navigateToPage(OnboardingOverview, { clientId: client.clientId });
    }
  }, [navigateToPage, client.clientId, needsFullIntake]);

  return (
    <>
      <Card
        className={cn(
          'rounded-2xl border border-[#e8e0ff] bg-white transition-shadow',
          'hover:shadow-md',
        )}
      >
        <CardHeader className="px-4 pb-1 pt-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {clientName != '-' ? (
                <ClientNameWrapper
                  client={client}
                  sessionAdvisor={sessionAdvisor}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="text-primary-dark truncate font-semibold">{clientName}</p>
                    {client.isSampleClient && (
                      <Badge variant="warning">
                        <FlaskConical />
                        Sample Client
                      </Badge>
                    )}
                  </div>
                </ClientNameWrapper>
              ) : (
                <p className="text-sm font-medium text-gray-700">Client Invited</p>
              )}

              <div className="mt-1 flex items-start gap-1.5 text-xs text-gray-700">
                <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="break-all">{client.clientEmail}</span>
              </div>

              {partner && (
                <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-gray-700">
                  <Users className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">Coupled with {partner}</span>
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-muted/60 h-8 w-8 rounded-full"
              onClick={e => {
                e.stopPropagation();
                onMenuClick();
              }}
            >
              <MoreVertical className="h-4.5 w-4.5 text-gray-700" />
            </Button>
          </div>
        </CardHeader>

        <CardContent
          className={cn(
            'space-y-2 px-3 pb-3 transition-all',
            !canShowPrimaryAction && !isExpanded && 'pb-1',
          )}
        >
          {isExpanded && (
            <div className="mt-3 space-y-3 rounded-xl bg-[#faf8ff] px-3 py-2 text-xs">
              {org && (
                <div>
                  <p className="mb-1 text-gray-700">Organization</p>
                  <p className="font-medium text-gray-800">{org}</p>
                </div>
              )}

              <div className="relative">
                <p className="mb-1 text-gray-700">Relationship Stage</p>
                <SelectClientRelationshipStage
                  clientRelationshipStage={client.clientRelationshipStage}
                  client={client}
                  triggerClassName="h-7 w-full rounded-full border border-[#e8e0ff] bg-white"
                />
              </div>

              <div className="relative">
                <p className="mb-1 text-gray-700">Statuses</p>
                <ActionTags
                  clientTags={client.clientTags}
                  client={client}
                />
              </div>

              <div className="relative">
                <p className="mb-1 text-gray-700">Referral Source</p>
                <SelectReferralSource
                  selectedReferralSourceId={client.clientReferralSourceId}
                  addInlineOption
                  includeFieldLabel={false}
                  placeholderText="Undefined"
                  triggerClassName="h-7 w-full rounded-full border border-[#e8e0ff] bg-white"
                  disabled={client.advisorId !== sessionAdvisor.advisorId}
                  onChangeReferralSourceId={value =>
                    dispatch(
                      putClientReferralSourceByClientIdRequest({
                        clientId: client.clientId,
                        advisorReferralSourceId: value,
                        advisorId: sessionAdvisor.advisorId,
                      }),
                    )
                  }
                  advisor={getClientAdvisor(client, sessionAdvisor, subordinateAdvisors)}
                  client={client}
                />
              </div>
            </div>
          )}
        </CardContent>

        {(org || client.clientTags.length > 0) && (
          <CardFooter className="flex flex-col justify-center gap-2 px-3 pb-3">
            {canShowPrimaryAction && (
              <Button
                variant="outline"
                size="sm"
                className="flex w-full items-center justify-center gap-2"
                onClick={handleViewResults}
              >
                <FileTextIcon className="size-4" />
                View Results
              </Button>
            )}
            <Button
              onClick={() => setIsExpanded(v => !v)}
              variant="ghost"
              size="sm"
              className="w-full bg-gray-100"
            >
              <span>{isExpanded ? 'Hide details' : 'More details'}</span>
              <ChevronDown
                className={cn('size-3 transition-transform', isExpanded && 'rotate-180')}
              />
            </Button>
          </CardFooter>
        )}
      </Card>
      <IncompleteIntakeDialog
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
        client={client}
      />
    </>
  );
}
